import { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAuth } from '../auth';
import { useUnread } from '../lib/use-unread';
import type { UnreadRequestSummary } from '../types';
import logoUrl from '../assets/cpe-logo.png';

export function AppLayout() {
  const { me, logout } = useAuth();
  const nav = useNavigate();
  const unreadQ = useUnread();
  const totalUnread = unreadQ.data?.totalUnread ?? 0;
  const requests = unreadQ.data?.requests ?? [];
  const [popoverOpen, setPopoverOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!popoverOpen) return;
    const onClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setPopoverOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPopoverOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [popoverOpen]);

  if (!me) return null;

  const isStaff = me.roles.includes('admin') || me.roles.includes('customer_service');

  const onLogout = async () => {
    await logout();
    nav('/login', { replace: true });
  };

  const onBadgeClick = () => {
    if (requests.length === 0) return;
    if (requests.length === 1 && requests[0]) {
      nav(`/request/${requests[0].requestId}`);
      return;
    }
    setPopoverOpen((v) => !v);
  };

  const goTo = (r: UnreadRequestSummary) => {
    setPopoverOpen(false);
    nav(`/request/${r.requestId}`);
  };

  return (
    <div className="min-h-[100dvh] flex flex-col bg-slate-50 text-base">
      <header className="bg-brand-700 text-white sticky top-0 z-20 shadow-md">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between gap-3 relative">
          <div className="flex items-center gap-2">
            <Link to="/" className="flex items-center gap-2.5 group">
              <img
                src={logoUrl}
                alt="CPE"
                className="h-8 sm:h-10 w-auto object-contain shrink-0 bg-white rounded-md p-1"
              />
              <span className="font-bold text-xl sm:text-2xl">Créditos</span>
            </Link>
            {totalUnread > 0 && (
              <button
                type="button"
                onClick={onBadgeClick}
                aria-label={`${totalUnread} mensaje(s) sin leer`}
                title={
                  requests.length === 1
                    ? `Ir al chat de ${requests[0]?.claimNumber}`
                    : `Ver ${totalUnread} mensaje(s) sin leer`
                }
                className="inline-flex items-center justify-center min-w-[36px] h-9 px-2.5 bg-rose-500 hover:bg-rose-600 active:bg-rose-700 text-white text-base font-bold rounded-full shadow transition"
              >
                {totalUnread > 99 ? '99+' : totalUnread}
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            {me.roles.includes('admin') && (
              <Link
                to="/admin"
                className="px-3 py-2 bg-brand-600 hover:bg-brand-500 active:bg-brand-800 rounded-lg text-sm font-semibold"
                title="Administración"
              >
                ⚙️ Admin
              </Link>
            )}
            <div className="text-right hidden sm:block">
              <div className="font-semibold text-base">{me.name}</div>
              <div className="opacity-80 text-xs">
                {isStaff ? me.roles.join(', ') : me.stores.map((s) => s.name).join(', ')}
              </div>
            </div>
            <button
              onClick={onLogout}
              className="px-4 py-2 bg-brand-600 hover:bg-brand-500 active:bg-brand-800 rounded-lg text-sm font-semibold"
            >
              Salir
            </button>
          </div>

          {popoverOpen && requests.length > 1 && (
            <div
              ref={popoverRef}
              className="absolute top-full left-3 right-3 sm:left-5 sm:right-auto sm:w-[26rem] mt-2 bg-white text-slate-900 rounded-2xl shadow-xl border border-slate-200 overflow-hidden z-30"
            >
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-rose-50">
                <span className="font-bold text-rose-900">
                  💬 {totalUnread} mensaje{totalUnread === 1 ? '' : 's'} sin leer
                </span>
                <button
                  type="button"
                  onClick={() => setPopoverOpen(false)}
                  aria-label="Cerrar"
                  className="text-rose-700 hover:text-rose-900 text-2xl leading-none"
                >
                  ×
                </button>
              </div>
              <ul className="max-h-[60dvh] overflow-y-auto divide-y divide-slate-100">
                {requests.map((r) => (
                  <li key={r.requestId}>
                    <button
                      type="button"
                      onClick={() => goTo(r)}
                      className="w-full text-left px-4 py-3 hover:bg-rose-50 active:bg-rose-100 transition flex gap-3 items-start"
                    >
                      <span className="shrink-0 inline-flex items-center justify-center min-w-[32px] h-8 px-2 bg-rose-500 text-white text-sm font-bold rounded-full">
                        {r.count > 99 ? '99+' : r.count}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-base text-slate-900 truncate">
                          {r.claimNumber}
                        </div>
                        <div className="text-sm text-slate-500 truncate">
                          {r.storeName} · hace {formatDistanceToNow(new Date(r.lastMessageAt), { locale: es })}
                        </div>
                        {r.lastMessagePreview && (
                          <div className="text-sm text-slate-700 mt-1 line-clamp-2">
                            {r.lastMessagePreview}
                          </div>
                        )}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </header>
      <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-5 sm:py-7">
        <Outlet />
      </main>
    </div>
  );
}
